import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * Interactive overlay that reacts to user input on top of the Background.
 *
 *   • Desktop (mouse): a soft green "cursor aura" follows the pointer with
 *     screen-blend so the honeycomb grid behind it brightens. The aura
 *     fades out when the cursor leaves the window.
 *   • Mobile / Touch: every touch/tap drops a multi-stage neon ripple
 *     (radial flash + two expanding concentric rings) at the contact point.
 *     Pointer-down on desktop also emits a ripple for click feedback.
 *
 * Sits between the Background layer and the page content (z-index 1) and
 * is fully `pointer-events: none` so it never blocks clicks.
 */
export default function InteractiveBackgroundLayer() {
  const [ripples, setRipples] = useState([]);
  const auraRef = useRef(null);
  const idRef = useRef(0);

  const spawnRipple = useCallback((x, y) => {
    idRef.current += 1;
    const id = idRef.current;
    setRipples((prev) => [...prev, { id, x, y }]);
    // Remove after the longest animation duration.
    window.setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 1500);
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      // Only mouse pointers move the cursor aura; touch uses ripples.
      if (e.pointerType !== "mouse") return;
      const aura = auraRef.current;
      if (!aura) return;
      aura.style.setProperty("--ss-mx", `${e.clientX}px`);
      aura.style.setProperty("--ss-my", `${e.clientY}px`);
      aura.style.opacity = "1";
    };

    const handleLeave = () => {
      const aura = auraRef.current;
      if (aura) aura.style.opacity = "0";
    };

    const handleDown = (e) => {
      spawnRipple(e.clientX, e.clientY);
    };

    window.addEventListener("pointermove", handleMove, { passive: true });
    window.addEventListener("pointerleave", handleLeave);
    window.addEventListener("pointerdown", handleDown, { passive: true });
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerleave", handleLeave);
      window.removeEventListener("pointerdown", handleDown);
    };
  }, [spawnRipple]);

  return (
    <div className="ss-interact-layer" aria-hidden="true" data-testid="interactive-bg">
      <div ref={auraRef} className="ss-cursor-aura" />
      {ripples.map((r) => (
        <React.Fragment key={r.id}>
          <span
            className="ss-ripple-flash"
            style={{ left: `${r.x}px`, top: `${r.y}px` }}
          />
          <span
            className="ss-ripple ss-ripple-1"
            style={{ left: `${r.x}px`, top: `${r.y}px` }}
          />
          <span
            className="ss-ripple ss-ripple-2"
            style={{ left: `${r.x}px`, top: `${r.y}px` }}
          />
        </React.Fragment>
      ))}
    </div>
  );
}
